import { DateConstant, DateConstantConfig, AnalyticsDataPoint } from '../types/analytics.types';

/**
 * Date Constants Service for Agent Experience (AX)
 * 
 * This service provides symbolic date constants that AI agents can use
 * to create temporally consistent example data without calculating actual dates.
 * 
 * Benefits for AI Agents:
 * - No need to calculate specific dates
 * - Consistent temporal relationships
 * - Self-documenting time periods
 * - Automatic adjustment to current date context
 */
export class DateConstantsService {
  
  /**
   * Predefined date constants with their calculation rules
   */
  private static readonly DATE_CONSTANTS: DateConstantConfig[] = [
    // Current period references
    { id: 'TODAY', description: 'Current day', daysOffset: 0, dateCalculation: 'exact_offset' },
    { id: 'YESTERDAY', description: 'Previous day', daysOffset: -1, dateCalculation: 'exact_offset' },
    
    // Week-based references
    { id: 'LAST_WEEK', description: '7 days ago', daysOffset: -7, dateCalculation: 'exact_offset' },
    { id: 'TWO_WEEKS_AGO', description: '14 days ago', daysOffset: -14, dateCalculation: 'exact_offset' },
    { id: 'CURRENT_WEEK_START', description: 'Start of current week', dateCalculation: 'start_of_period', period: 'week' },
    
    // Month-based references  
    { id: 'LAST_MONTH', description: '30 days ago', daysOffset: -30, dateCalculation: 'exact_offset' },
    { id: 'TWO_MONTHS_AGO', description: '60 days ago', daysOffset: -60, dateCalculation: 'exact_offset' },
    { id: 'CURRENT_MONTH_START', description: 'Start of current month', dateCalculation: 'start_of_period', period: 'month' },
    
    // Quarter and longer periods
    { id: 'LAST_QUARTER', description: '90 days ago', daysOffset: -90, dateCalculation: 'exact_offset' },
    { id: 'SIX_MONTHS_AGO', description: '180 days ago', daysOffset: -180, dateCalculation: 'exact_offset' },
    { id: 'LAST_YEAR', description: '365 days ago', daysOffset: -365, dateCalculation: 'exact_offset' },
    { id: 'CURRENT_QUARTER_START', description: 'Start of current quarter', dateCalculation: 'start_of_period', period: 'quarter' },
    { id: 'CURRENT_YEAR_START', description: 'Start of current year', dateCalculation: 'start_of_period', period: 'year' },
    
    // Period-to-Date (UI presets end at yesterday UTC; today excluded)
    { id: 'WTD', description: 'Week-to-Date (Sunday through yesterday UTC, excludes today)', dateCalculation: 'period_to_date', period: 'week' },
    { id: 'MTD', description: 'Month-to-Date (1st of month through yesterday UTC, excludes today)', dateCalculation: 'period_to_date', period: 'month' },
    { id: 'YTD', description: 'Year-to-Date (Jan 1 through yesterday UTC, excludes today)', dateCalculation: 'period_to_date', period: 'year' }
  ];

  /**
   * Resolve a date constant to an actual ISO date string
   */
  static resolveDateConstant(constant: DateConstant, referenceDate?: Date): string {
    const config = this.DATE_CONSTANTS.find(c => c.id === constant);
    if (!config) {
      throw new Error(`Unknown date constant: ${constant}`);
    }

    const baseDate = referenceDate || new Date();
    const resolvedDate = new Date(baseDate);

    if (config.daysOffset !== undefined) {
      // Simple offset calculation using UTC
      resolvedDate.setUTCDate(baseDate.getUTCDate() + config.daysOffset);
      resolvedDate.setUTCHours(0, 0, 0, 0); // Start of day for consistency
    } else if (config.dateCalculation === 'start_of_period' && config.period) {
      // Calculate start of period
      this.setToStartOfPeriod(resolvedDate, config.period);
    } else if (config.dateCalculation === 'period_to_date' && config.period) {
      // Resolves to period start only; pair with YESTERDAY/TODAY in UI for full range
      this.setToStartOfPeriod(resolvedDate, config.period);
    }

    const result = resolvedDate.toISOString();
    return result;
  }

  /**
   * Set date to start of specified period using UTC
   */
  private static setToStartOfPeriod(date: Date, period: string): void {
    switch (period) {
      case 'week':
        // Set to start of week (Sunday) using UTC
        const dayOfWeek = date.getUTCDay();
        date.setUTCDate(date.getUTCDate() - dayOfWeek);
        date.setUTCHours(0, 0, 0, 0);
        break;
      
      case 'month':
        date.setUTCDate(1);
        date.setUTCHours(0, 0, 0, 0);
        break;
      
      case 'quarter':
        const currentMonth = date.getUTCMonth();
        const quarterStartMonth = Math.floor(currentMonth / 3) * 3;
        date.setUTCMonth(quarterStartMonth, 1);
        date.setUTCHours(0, 0, 0, 0);
        break;
      
      case 'year':
        date.setUTCMonth(0, 1);
        date.setUTCHours(0, 0, 0, 0);
        break;
      
      default:
        throw new Error(`Unsupported period: ${period}`);
    }
  }

  /**
   * Process example data array and resolve any date constants
   */
  static resolveDataDates(data: AnalyticsDataPoint[], referenceDate?: Date): AnalyticsDataPoint[] {
    return data.map(row => {
      if (row.date && typeof row.date === 'string' && this.isDateConstant(row.date)) {
        return {
          ...row,
          date: this.resolveDateConstant(row.date as DateConstant, referenceDate)
        };
      }
      return row;
    });
  }

  /**
   * Check if a string is a valid date constant
   */
  static isDateConstant(value: string): value is DateConstant {
    return this.DATE_CONSTANTS.some(config => config.id === value);
  }

  /**
   * Get all available date constants with descriptions (useful for AI prompts)
   */
  static getAvailableConstants(): Array<{id: DateConstant, description: string}> {
    return this.DATE_CONSTANTS.map(config => ({
      id: config.id,
      description: config.description
    }));
  }

  /**
   * Generate example date distribution for AI agents
   * This provides a template showing how to distribute data across time periods
   */
  static generateExampleDistribution(): Array<{timeFrame: string, constants: DateConstant[], description: string}> {
    return [
      {
        timeFrame: 'Recent (Last 7 days)',
        constants: ['TODAY', 'YESTERDAY'],
        description: 'Use for current activity data that should appear in all date filters'
      },
      {
        timeFrame: 'Medium term (8-30 days)',
        constants: ['LAST_WEEK', 'TWO_WEEKS_AGO'],
        description: 'Use for data that should appear in 30-day and 90-day filters'
      },
      {
        timeFrame: 'Long term (31+ days)',
        constants: ['LAST_MONTH', 'TWO_MONTHS_AGO', 'LAST_QUARTER'],
        description: 'Use for historical data that should only appear in 90-day+ filters'
      }
    ];
  }
} 